import { useEffect, useRef, useState } from 'react';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Check, Loader2, Search, X } from 'lucide-react';
import { cn } from '../../../../shared';
import { lookupPostings, type LeverPosting } from '../../services/leverApi';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Pick a Lever posting by searching, instead of pasting a UUID.
 *
 * Accepts three inputs, because a TA manager may have any of them to hand:
 *  - a role name ("sales") → title/team/location search,
 *  - a candidate's email   → the postings they applied to,
 *  - a pasted posting id or Lever URL → used directly.
 *
 * Falls back to plain manual entry whenever Lever is unreachable, so a dead API
 * key degrades the convenience without blocking the form.
 */
export function PostingPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (postingId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LeverPosting[]>([]);
  const [chosen, setChosen] = useState<LeverPosting | null>(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const latest = useRef('');

  // onChange is deliberately NOT an effect dependency, and the effect no longer
  // calls it: doing so re-rendered the parent, which produced a new onChange,
  // which re-ran the effect — an infinite loop as soon as a posting id was pasted.
  // The paste case belongs in the change handler, where props are current anyway.
  useEffect(() => {
    const q = query.trim();
    latest.current = q;
    if (q.length < 2) return;
    // A pasted id needs no lookup; handleQueryChange already took it.
    if (UUID.test(q)) return;

    const timer = window.setTimeout(() => {
      setLoading(true);
      // Title search only. The candidate-email lookup this used to attempt found
      // nothing in practice, and leaving it in meant typing an address returned an
      // empty list with no explanation — worse than not offering it. Removed here
      // as well as in the help text, so the two cannot disagree.
      void lookupPostings({ query: q })
        .then((res) => {
          if (latest.current !== q) return;
          setResults(res.postings);
          setUnavailable(res.available ? null : res.error || 'Lever unavailable');
        })
        .finally(() => {
          if (latest.current === q) setLoading(false);
        });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const valid = UUID.test((value || '').trim());
  const suggestions = query.trim().length < 2 ? [] : results;

  /** Take a pasted posting id or Lever URL immediately, without a lookup. */
  function handleQueryChange(next: string) {
    setQuery(next);
    setOpen(true);
    const pasted = UUID.exec(next.trim());
    if (pasted) onChange(pasted[0].toLowerCase());
  }

  function choose(p: LeverPosting) {
    onChange(p.id);
    setChosen(p);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={boxRef} className="relative block">
      <span className="mb-1 block text-xs font-medium text-gray-700">
        Lever posting <span className="text-red-600">*</span>
      </span>

      {valid ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-300 bg-gray-50 px-2.5 py-2">
          <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
          <span className="min-w-0 flex-1 text-sm text-gray-900">
            {chosen ? (
              <>
                {chosen.title}
                <span className="ml-1.5 text-xs text-gray-500">
                  {chosen.team ? `${chosen.team} · ` : ''}
                  {chosen.state}
                </span>
              </>
            ) : (
              <code className="text-xs">{value}</code>
            )}
          </span>
          <button
            type="button"
            aria-label="Clear posting"
            onClick={() => {
              onChange('');
              setChosen(null);
            }}
            className="text-gray-400 hover:text-gray-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder="Search a role, a candidate email, or paste a posting id"
            className="pl-8"
          />
          {loading ? (
            <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
          ) : null}
        </div>
      )}

      {!valid ? (
        <span className="mt-1 block text-xs text-gray-500">
          Type a role name to find the posting.
        </span>
      ) : null}

      {open && !valid && query.trim().length >= 2 ? (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          {suggestions.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => choose(p)}
              className="block w-full px-3 py-2 text-left hover:bg-gray-100"
            >
              <span className="block truncate text-sm text-gray-900">{p.title}</span>
              <span className="block truncate text-xs text-gray-500">
                {[p.team, p.location, p.state].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}

          {!loading && suggestions.length === 0 && !unavailable ? (
            <p className="px-3 py-2 text-xs text-gray-500">
              {query.includes('@') ? 'No applications found for that email.' : 'No postings match.'}
            </p>
          ) : null}

          {unavailable ? (
            <p className={cn('px-3 py-2 text-[11px] text-[#854F0B]', 'bg-[#FCEEE2]')}>
              Lever lookup unavailable ({unavailable}) — paste the posting id from the URL instead.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
